function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { Link } from "react-router-dom";
import { withRouter } from "react-router";
import { Container, FlexboxContainer, CssContainer } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill, Empty, Search, Filters, Delta, Pending, Segmented } from "../../components/analytics-kit";

// territory-common deltas ({text, dir, pct}) in the kit's shape; null stays null ("no comparison").
const kitDelta = d => d && d.pct != null ? {
  dir: d.dir,
  pct: d.pct
} : null;
import ComparisonPicker from "../../components/comparison-picker";
import StatusFilter from "../../components/status-filter";
import PlunkDropDown from "../../components/plunk-dropdown";
import { Loader, ChartLoader } from "../../components/loaders";
import Error from "../../components/error";
import Chart from "../../components/chart";
import { hasTenantFeature } from "../../functions/tenant-features";
import RangePicker from "../../components/range-picker";
import { rangeLabelFor, RANGES, num, usd, aov, fetchSales, bucketFor, trendLabel, stateName, deltaOf, deltaOfPaced, comparisonQs, hasPrevTrend, PREV_LINE_COLOR, channelLabel, apiComparison, parseCustomRange, readBasis, titleCase, cleanTitle } from "./territory-common";
import { money, placeName, isHouseRep } from "./territory-redesign-kit";
import account from "../../data/account";
import { ActivityComparisonMenu } from "../../components/container-activity-menus";
import { canViewQuotes, listQuotes, listOrders } from "./sales-quotes-api";
import compLabel from "../../functions/comp-label";
import { getGlobalRange, getGlobalComp, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";
import PlanCard from "./plan-card";
import { getDealerScorecard, getDealerProfile, getDealerLifetime, getDealerTimeline, getDealerNotes, getGeoHeatCells, getPeerProducts } from "./sales-api";
import { HealthBlock, LifetimeStrip, OpenBusinessCard, PeopleCard, CategoryMixCard, WebSignalCard, TimelineCard, addrText, fmtDay } from "./dealer-cards";

// Short place for a header line: "Miami, FL" from an address object, or the string as given.
function shortPlace(a) {
  if (!a) return "";
  if (typeof a === "string") return a.trim();
  const city = a.city ? titleCase(a.city) : "";
  return [city, a.region || a.state].filter(Boolean).join(", ");
}
// Region code of an address object (bill-to or ship-to), for the market link.
function regionOf(a) {
  return a && typeof a === "object" ? String(a.region || a.state || "").trim() : "";
}
// Plain words for the lapse band next to the last order.
const RHYTHM = {
  healthy: "on their usual rhythm",
  watch: "a little later than usual",
  at_risk: "later than usual",
  lapsing: "well past their usual rhythm"
};

/*
 * Customer (Dealer) Detail, §5 of the Territory & Rep Intelligence spec
 * ("orders, products, rep, store/territory, trend" for one business
 * customer).
 *
 * Route: /Territory/:range/customer/:id, opened from the overview Top
 * Dealers table, the market-detail dealer table, the rep-detail sold-to
 * table, and the cross-sold accounts table. :id is the dealer ACCOUNT NUMBER.
 *
 * Server-scoped via the 1.1.5/1.1.7 drill-through params:
 *   /sales/totals/{range}?customer=X  → KPI header (note `reps` = how many
 *                                       distinct reps sold to this account)
 *   /sales/trend/{range}?customer=X   → dealer sales trend (1.1.7); daily for
 *                                       windows over 60 days, regrained here
 *   /sales/customer/{range}/{X}       → per-product breakdown
 *   /sales/customers/{range}?top=2000 → identity row (name, city/region,
 *                                       assigned rep); server caps at 2000,
 *                                       which covers every active dealer today
 *
 * Fail-soft rails (each card hides or shows a calm line when its rail answers
 * null; see dealer-cards.jsx):
 *   /sales/scorecards/dealers/{range}/{X} → Health block + Plan card + Category mix
 *   /sales/dealers/{range}/{X}            → People (profile, assigned rep, contacts)
 *   /sales/dealers/lifetime/{X}           → Lifetime strip
 *   /sales/dealers/timeline/{X}, /notes   → Timeline card
 *   /sales/geo/heat/{range}?metric=sessions → Web signal (the account's ZIP3 cell)
 *   /sales/copurchase?product=            → "Peers buy, this account doesn't"
 *   /sales/quotes, /sales/quotes/orders   → Open business (quotes feature)
 */

// Window length in days: windows over 60 days fetch the daily series and offer a Day / Week / Month grain.
function rangeDays(range) {
  const w = parseCustomRange(range);
  if (w) return Math.round((w[1] - w[0]) / 864e5) + 1;
  if (/today|yesterday/.test(range)) return 1;
  if (/7_days|week/.test(range)) return 7;
  if (/month/.test(range)) return 31;
  if (/quarter/.test(range)) return 92;
  if (/year/.test(range)) return 366;
  return 3650; // all_time
}
const pad2 = v => String(v).padStart(2, "0");
const isoOf = d => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
function periodKey(period, grain) {
  const [y, m, d] = String(period).slice(0, 10).split("-").map(Number);
  if (grain === "month") return `${y}-${pad2(m)}-01`;
  const dt = new Date(y, (m || 1) - 1, d || 1);
  dt.setDate(dt.getDate() - (dt.getDay() + 6) % 7); // Monday
  return isoOf(dt);
}
// Client-side regrain of the daily series (sums; orders are per-day counts so the week/month sums hold).
function regrain(rows, grain) {
  if (!rows || grain === "day") return rows;
  const map = new Map();
  rows.forEach(t => {
    const key = periodKey(t.period, grain);
    const cur = map.get(key) || {
      period: key,
      totalAmount: 0,
      orders: 0,
      quantity: 0,
      prevTotalAmount: 0,
      prevOrders: 0,
      prevQuantity: 0
    };
    cur.totalAmount += Number(t.totalAmount) || 0;
    cur.orders += Number(t.orders) || 0;
    cur.quantity += Number(t.quantity) || 0;
    cur.prevTotalAmount += Number(t.prevTotalAmount) || 0;
    cur.prevOrders += Number(t.prevOrders) || 0;
    cur.prevQuantity += Number(t.prevQuantity) || 0;
    map.set(key, cur);
  });
  return Array.from(map.values()).sort((a, b) => a.period < b.period ? -1 : a.period > b.period ? 1 : 0);
}
class CustomerDetailViewComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onRange", e => {
      this.props.history.push(`/Territory/${e.target.value}/customer/${encodeURIComponent(this.customerId())}`);
    });
    _defineProperty(this, "onCompSelected", e => {
      const comp = e.detail.item.dataset.value;
      if (comp === "unavailable" || comp === this.state.comp) return;
      this.setState({
        comp,
        data: null,
        error: false
      }, () => this.load());
    });
    _defineProperty(this, "back", () => {
      this.props.history.push(`/Territory/${this.range()}`);
    });
    _defineProperty(this, "openRep", repCode => {
      if (!repCode) return;
      this.props.history.push(`/Territory/${this.range()}/rep/${encodeURIComponent(repCode)}`);
    });
    _defineProperty(this, "openRegion", code => {
      if (!code) return;
      this.props.history.push(`/Territory/${this.range()}/region/${encodeURIComponent(code)}`);
    });
    _defineProperty(this, "openProduct", p => {
      if (!p || !p.productId) return;
      this.props.history.push({
        pathname: `/Territory/${this.range()}/product/${encodeURIComponent(p.productId)}`,
        state: p.title ? {
          title: p.title
        } : undefined
      });
    });
    this.state = {
      data: null,
      error: false,
      status: [],
      comp: getGlobalComp("prev_period"),
      grain: null,
      x: {},
      allProducts: false
    };
    this._alive = true;
    this._gen = 0;
  }
  range() {
    return this.props.params && this.props.params.range || getGlobalRange();
  }
  customerId() {
    return decodeURIComponent(this.props.params && this.props.params.id || "");
  }
  componentDidMount() {
    this.load();
    this._periodTok = subscribePeriod(({
      range,
      comp
    }) => {
      if (!this._alive || !range) return;
      if (comp && comp !== this.state.comp) this.setState({
        comp
      });
      const target = `/Territory/${range}/customer/${encodeURIComponent(this.customerId())}`;
      if (window.location.pathname === target) {
        this.setState({
          data: null,
          error: false
        }, () => this.load());
      } else {
        this.props.history.push(target);
      }
    });
  }
  componentWillUnmount() {
    this._alive = false;
    unsubscribePeriod(this._periodTok);
  }
  componentDidUpdate(prev) {
    const prevKey = `${prev.params && prev.params.range || ""}|${prev.params && prev.params.id || ""}`;
    const key = `${this.props.params && this.props.params.range || ""}|${this.props.params && this.props.params.id || ""}`;
    if (prevKey !== key) {
      this.setState({
        data: null,
        error: false,
        grain: null
      });
      this.load();
    }
  }

  // Fail-soft extras land one by one under state.x[key]: undefined = loading, null = rail not there.
  setX(gen, key, value) {
    if (!this._alive || gen !== this._gen) return;
    this.setState(s => ({
      x: {
        ...s.x,
        [key]: value
      }
    }));
  }
  soft(gen, key, p) {
    p.then(v => this.setX(gen, key, v === undefined ? null : v)).catch(() => this.setX(gen, key, null));
  }
  async load() {
    const range = this.range();
    const comp = this.state.comp;
    const rawId = this.customerId();
    const id = encodeURIComponent(rawId);
    const longRange = rangeDays(range) > 60;
    const bucket = longRange ? "day" : bucketFor(range);
    const st = (this.state.status || []).join(",");
    const ws = u => st ? u + (u.includes("?") ? "&" : "?") + "status=" + encodeURIComponent(st) : u;
    const gen = ++this._gen;
    this.setState({
      x: {}
    });
    // Rails that do not need the primary load: start them now so the page fills in one pass.
    this.soft(gen, "card", getDealerScorecard(range, rawId, {
      comparison: apiComparison(comp)
    }));
    this.soft(gen, "prof", getDealerProfile(range, rawId));
    this.soft(gen, "life", getDealerLifetime(rawId));
    this.soft(gen, "timeline", getDealerTimeline(rawId, range, 30));
    this.soft(gen, "notes", getDealerNotes(rawId));
    this.soft(gen, "heat", getGeoHeatCells(range, "sessions"));
    if (hasTenantFeature("quotes")) {
      this.soft(gen, "quotes", listQuotes({
        customerId: rawId,
        status: "open"
      }));
      this.soft(gen, "orders", listOrders({
        customerId: rawId
      }));
    } else {
      this.setX(gen, "quotes", null);
      this.setX(gen, "orders", null);
    }
    try {
      const [totals, trend, products, roster, reps] = await Promise.all([fetchSales(ws(`/sales/totals/${range}?customer=${id}${comparisonQs(range, false, comp)}`)), fetchSales(ws(`/sales/trend/${range}?bucket=${bucket}&customer=${id}${comparisonQs(range, false, comp)}`)), fetchSales(ws(`/sales/customer/${range}/${id}`)),
      // Identity row; the basis (bill | ship, set on the Territory pages) picks which address the city/region come from.
      fetchSales(`/sales/customers/${range}?top=2000&basis=${readBasis((account.get() || {}).tenantId) === "ship" ? "ship" : "bill"}`), fetchSales(`/sales/reps/${range}`).catch(() => [])]);
      if (!this._alive || gen !== this._gen) return;
      this.setState({
        data: {
          totals,
          trend,
          products,
          roster,
          reps,
          bucket,
          longRange
        }
      });
      // Peers need the account's top products (co-purchase is product-keyed).
      const top = (products || []).slice().sort((a, b) => (Number(b.totalAmount) || 0) - (Number(a.totalAmount) || 0));
      this.soft(gen, "peers", getPeerProducts(top.slice(0, 3), top.map(p => p.productId), 8));
    } catch (e) {
      if (this._alive && gen === this._gen) this.setState({
        error: true
      });
    }
  }
  trendChartData(trend, bucket) {
    if (!trend || trend.length < 2) return null;
    const withPrev = hasPrevTrend(trend);
    const data = [withPrev ? ["Period", "Revenue", "Prev Revenue", "Orders", "Prev Orders"] : ["Period", "Revenue", "Orders"]];
    trend.forEach(t => {
      const rev = Math.round((Number(t.totalAmount) || 0) * 100) / 100;
      const second = Number(t.orders) || 0;
      if (withPrev) data.push([trendLabel(t.period, bucket), rev, Math.round((Number(t.prevTotalAmount) || 0) * 100) / 100, second, Number(t.prevOrders) || 0]);else data.push([trendLabel(t.period, bucket), rev, second]);
    });
    data.withPrev = withPrev;
    return data;
  }
  render() {
    if (!hasTenantFeature("territoryRep")) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Territory & Rep Intelligence is not configured for this workspace."));
    }
    const {
      data,
      error,
      x
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    if (!data) return /*#__PURE__*/React.createElement(Loader, null);
    const id = this.customerId();
    const range = this.range();
    const norm = v => String(v || "").trim();
    const {
      totals,
      products,
      roster,
      longRange
    } = data;
    const dealer = roster.find(c => norm(c.accountNumber) === norm(id)) || {
      accountNumber: id,
      name: id
    };
    const allProducts = (products || []).slice().sort((a, b) => (Number(b.totalAmount) || 0) - (Number(a.totalAmount) || 0));
    const topProducts = this.state.allProducts ? allProducts : allProducts.slice(0, 15);
    // Grain: windows over 60 days carry the daily series and regrain here (default = the usual bucket for the window).
    const grain = longRange ? this.state.grain || bucketFor(range) : data.bucket;
    const trend = longRange ? regrain(data.trend, grain) : data.trend;
    const chartData = this.trendChartData(trend, grain);
    const location = [dealer.city, dealer.region].filter(Boolean).join(", ");
    const postal = String(dealer.postalCode || x.prof && x.prof.profile && x.prof.profile.shipTo && typeof x.prof.profile.shipTo === "object" && x.prof.profile.shipTo.postalCode || "").trim();
    const zip3 = postal.replace(/\D/g, "").slice(0, 3);
    const heatCell = x.heat === undefined ? undefined : x.heat && zip3 ? x.heat.find(c => String(c.zip3) === zip3) || null : null;
    const showQuotes = hasTenantFeature("quotes");
    // Both addresses from the dealer profile: head office (bill-to) and delivery (ship-to).
    const prof = x.prof && x.prof.profile || null;
    const billTo = prof && prof.billTo && addrText(prof.billTo) ? prof.billTo : null;
    const shipTo = prof && prof.shipTo && addrText(prof.shipTo) ? prof.shipTo : null;
    const billShort = shortPlace(billTo),
      shipShort = shortPlace(shipTo);
    const placeLine = billShort && shipShort && billShort.toLowerCase() !== shipShort.toLowerCase() ? `Head office ${billShort} · delivers to ${shipShort}` : billShort || shipShort || [dealer.city ? titleCase(dealer.city) : null, dealer.region].filter(Boolean).join(", ");
    const marketRegions = [...new Set([regionOf(billTo), regionOf(shipTo), dealer.region].map(r => String(r || "").trim().toUpperCase()).filter(Boolean))];
    const assignedHouse = isHouseRep({
      name: dealer.assignedRepName,
      repCode: dealer.assignedRepId
    });
    const assignedName = dealer.assignedRepId ? assignedHouse ? "House account" : titleCase(dealer.assignedRepName || dealer.assignedRepId) : null;
    const life = x.life;
    const health = x.card && x.card.health ? x.card.health : null;
    const lastOrder = life && life.lastOrder || health && health.lastOrder || "";
    // One "how often they order" per dealer: the lapse scorer's cadence (the Account panel's "usually every N
    // days" and the next-order date), falling back to the lifetime average gap only when there is no cadence.
    // The tile used the lifetime MEAN gap (5 days) next to the panel's cadence (7 days) for the same dealer.
    const cadenceDays = health && Number(health.cadenceDays) > 0 ? Math.round(Number(health.cadenceDays)) : null;
    const gapDays = cadenceDays != null ? cadenceDays : life && life.avgGapDays != null ? Math.round(Number(life.avgGapDays)) : null;
    const avgOrder = Number(totals.orders) > 0 ? (Number(totals.totalAmount) || 0) / Number(totals.orders) : 0;
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view territory-detail customer-detail tr-view wa-view"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        className: "tr-link",
        to: `/Territory/${range}`
      }, "Territory & Reps"), " \xB7 Dealer"),
      title: titleCase(dealer.name || id),
      subtitle: [`Account ${dealer.accountNumber || id}`, placeLine || null, assignedName ? assignedHouse ? "house account" : `rep ${assignedName}` : null, rangeLabelFor(range, RANGES)].filter(Boolean).join(" · "),
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, showQuotes && canViewQuotes() ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
        title: "Every quote for this account",
        onClick: () => this.props.history.push(`/Quotes?customerId=${encodeURIComponent(dealer.accountNumber || id)}&customer=${encodeURIComponent(dealer.name || "")}`)
      }, "Quotes"), /*#__PURE__*/React.createElement("button", {
        className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
        title: "Start a quote for this account",
        onClick: () => this.props.history.push(`/Quotes/new?customerId=${encodeURIComponent(dealer.accountNumber || id)}`)
      }, "New quote")) : null, /*#__PURE__*/React.createElement("span", {
        className: "dd-status",
        title: "Order statuses to include; none selected = all booked orders"
      }, /*#__PURE__*/React.createElement(StatusFilter, {
        range: range,
        value: this.state.status,
        width: 200,
        onChange: v => this.setState({
          status: v,
          data: null,
          error: false
        }, () => this.load())
      })))
    }), /*#__PURE__*/React.createElement(Ribbon, {
      comparing: true,
      cols: 4,
      tiles: [{
        l: "Revenue",
        v: money(totals.totalAmount),
        d: kitDelta(deltaOfPaced(totals.totalAmount, totals.prevTotalAmount, range)),
        s: avgOrder > 0 ? `${money(avgOrder)} average order` : "",
        t: "Booked revenue at this account in the period, with the change against the previous period paced to the days elapsed"
      }, {
        l: "Orders",
        v: num(totals.orders),
        d: kitDelta(deltaOfPaced(totals.orders, totals.prevOrders, range)),
        s: gapDays != null && gapDays > 0 ? gapDays === 1 ? "usually every day" : `usually every ${num(gapDays)} days` : "",
        t: "Orders this account placed in the period; the rhythm line is how often this account usually orders, the same cadence as the Account panel"
      }, lastOrder ? {
        l: "Last order",
        v: fmtDay(lastOrder),
        s: health && health.band ? RHYTHM[health.band] || "" : "",
        noDelta: true,
        t: "The account's most recent order, against its own usual ordering rhythm"
      } : {
        l: "Units",
        v: num(totals.quantity),
        d: kitDelta(deltaOfPaced(totals.quantity, totals.prevQuantity, range)),
        t: "Units this account bought in the period"
      },
      // No per-order rep ids (retail books, pre-ERP feeds): "Sold by 0 reps" reads as a bug, so show the average order instead (review §2.2).
      Number(totals.reps) > 0 ? {
        l: "Sold by",
        v: Number(totals.reps) === 1 ? "1 rep" : `${num(totals.reps)} reps`,
        s: Number(totals.reps) > 1 ? "different reps closed orders here" : assignedName && !assignedHouse ? `${assignedName} is their rep` : "",
        t: "How many different reps closed orders on this account in the period",
        noDelta: true
      } : {
        l: "Average order",
        v: avgOrder > 0 ? money(avgOrder) : "-----",
        t: "Revenue divided by orders in the period"
      }]
    }), /*#__PURE__*/React.createElement(LifetimeStrip, {
      life: x.life
    }), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, x.card !== undefined ? /*#__PURE__*/React.createElement(PlanCard, {
      kind: "dealer",
      id: id,
      range: range,
      comparison: apiComparison(this.state.comp),
      span: 12,
      card: x.card
    }) : null, /*#__PURE__*/React.createElement(Card, {
      title: "Account",
      hint: "click through to the market, rep or channel",
      span: chartData ? 4 : 12
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table customer-facts"
    }, /*#__PURE__*/React.createElement("tbody", null, (billTo || shipTo) && (!billTo || !shipTo || addrText(billTo) === addrText(shipTo)) ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "fact-label",
      title: "Head office and delivery address are the same on the customer record"
    }, "Address"), /*#__PURE__*/React.createElement("td", null, addrText(billTo || shipTo))) : billTo || shipTo ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "fact-label",
      title: "Bill-to address on the customer record"
    }, "Head office"), /*#__PURE__*/React.createElement("td", null, billTo ? addrText(billTo) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "same as delivery"))), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "fact-label",
      title: "Ship-to address on the customer record"
    }, "Delivers to"), /*#__PURE__*/React.createElement("td", null, shipTo ? addrText(shipTo) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "same as head office")))) : /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "fact-label",
      title: "City and state from the dealer address"
    }, "Location"), /*#__PURE__*/React.createElement("td", null, location ? [dealer.city ? titleCase(dealer.city) : null, dealer.region].filter(Boolean).join(", ") : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "not on file"))), marketRegions.length ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "fact-label",
      title: "Open the market page for the state or province"
    }, "Market"), /*#__PURE__*/React.createElement("td", null, marketRegions.map(r => /*#__PURE__*/React.createElement("button", {
      key: r,
      className: "rep-state-chip fact-chip",
      onClick: () => this.openRegion(r),
      title: `Open ${placeName(r)}`
    }, placeName(r))))) : null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "fact-label",
      title: "The rep on the account record; sold-by is used when order lines carry one"
    }, "Assigned Rep"), /*#__PURE__*/React.createElement("td", null, dealer.assignedRepId ? /*#__PURE__*/React.createElement("button", {
      className: "rep-state-chip fact-chip",
      onClick: () => this.openRep(dealer.assignedRepId),
      title: "Open this rep's detail"
    }, assignedName) : /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "none"))), (() => {
      // Dealer's sales channel = its assigned rep's channel
      // (the same attribution rule the rollup uses).
      const norm2 = v => String(v || "").trim();
      const assigned = (data.reps || []).find(r => norm2(r.repCode) === norm2(dealer.assignedRepId));
      if (!assigned || !assigned.channel) return null;
      return /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
        className: "fact-label",
        title: "The selling rep's channel tag"
      }, "Sales Channel"), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("button", {
        className: "rep-state-chip fact-chip",
        onClick: () => this.props.history.push(`/Territory/${range}/channel/${encodeURIComponent(assigned.channel)}`),
        title: `Open ${channelLabel(assigned.channel)} channel detail`
      }, channelLabel(assigned.channel))));
    })(), !billTo && !shipTo ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "fact-label",
      title: "Postal code from the dealer address"
    }, "Postal Code"), /*#__PURE__*/React.createElement("td", null, dealer.postalCode || /*#__PURE__*/React.createElement("span", {
      className: "dim"
    }, "not on file"))) : null, x.prof && x.prof.profile && x.prof.profile.stage ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
      className: "fact-label",
      title: "Lifecycle stage on the customer record"
    }, "Stage"), /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement(Pill, {
      tone: x.prof.profile.lapsed ? "warning" : "neutral"
    }, x.prof.profile.stage, x.prof.profile.lapsed ? " · lapsed" : ""))) : null)), /*#__PURE__*/React.createElement(HealthBlock, {
      card: x.card,
      range: range
    })), chartData && /*#__PURE__*/React.createElement(Card, {
      title: "Sales trend",
      hint: "revenue bars \xB7 orders line \xB7 dashed = prior period when a comparison is set",
      span: 8,
      right: longRange ? /*#__PURE__*/React.createElement("span", {
        title: "Regroup the daily series by day, week or month"
      }, /*#__PURE__*/React.createElement(Segmented, {
        options: [["day", "Day"], ["week", "Week"], ["month", "Month"]],
        value: grain,
        onChange: g => this.setState({
          grain: g
        })
      })) : null
    }, /*#__PURE__*/React.createElement("div", {
      className: "territory-trend"
    }, /*#__PURE__*/React.createElement(Chart, {
      width: "100%",
      height: "320px",
      chartType: "ComboChart",
      loader: /*#__PURE__*/React.createElement(ChartLoader, null),
      data: chartData,
      options: {
        seriesType: "bars",
        series: chartData.withPrev ? {
          0: {
            targetAxisIndex: 0,
            color: "#ff6358" /* accent */,
            tooltipFormat: "currency"
          },
          1: {
            type: "line",
            targetAxisIndex: 0,
            color: "#aa46be" /* palette purple */,
            lineDashStyle: [4, 3],
            tooltipFormat: "currency"
          },
          2: {
            type: "line",
            targetAxisIndex: 1,
            color: "#78d237" /* palette green */,
            tooltipFormat: "{0:n0}"
          },
          3: {
            type: "line",
            targetAxisIndex: 1,
            color: "#ffd246" /* palette yellow */,
            lineDashStyle: [4, 3],
            tooltipFormat: "{0:n0}"
          }
        } : {
          0: {
            targetAxisIndex: 0,
            color: "#ff6358" /* accent */,
            tooltipFormat: "currency"
          },
          1: {
            type: "line",
            targetAxisIndex: 1,
            color: "#78d237" /* palette green */,
            tooltipFormat: "{0:n0}"
          }
        },
        vAxes: {
          0: {
            title: "Revenue",
            format: "short"
          },
          1: {
            title: "Orders",
            format: "0"
          }
        },
        legend: {
          position: "top"
        },
        backgroundColor: "transparent",
        chartArea: {
          left: 70,
          right: 60,
          top: 40,
          bottom: 40
        }
      }
    }))), /*#__PURE__*/React.createElement(WebSignalCard, {
      cell: heatCell,
      span: 12,
      accountRevenue: totals ? Number(totals.totalAmount) || 0 : null
    }), showQuotes ? /*#__PURE__*/React.createElement(OpenBusinessCard, {
      quotes: x.quotes,
      orders: x.orders,
      span: 12
    }) : null, /*#__PURE__*/React.createElement(PeopleCard, {
      prof: x.prof,
      onOpenRep: this.openRep,
      span: 6
    }), /*#__PURE__*/React.createElement(CategoryMixCard, {
      card: x.card,
      peers: x.peers,
      onOpenProduct: this.openProduct,
      span: 6
    }), /*#__PURE__*/React.createElement(Card, {
      title: "What they buy",
      hint: "by revenue \xB7 click a product for its page",
      right: allProducts.length > 15 ? /*#__PURE__*/React.createElement("a", {
        className: "tr-link",
        href: "#",
        onClick: e => {
          e.preventDefault();
          this.setState({
            allProducts: !this.state.allProducts
          });
        }
      }, this.state.allProducts ? "Top 15 only" : `All ${num(allProducts.length)} products`) : null
    }, /*#__PURE__*/React.createElement("div", {
      className: "tblwrap"
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table dd-products"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", {
      title: "Product this account bought in the window; click for its detail"
    }, "Product"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Orders in the window that include this product"
    }, "Orders"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Units of this product bought in the window"
    }, "Units"), /*#__PURE__*/React.createElement("th", {
      className: "n",
      title: "Revenue booked on this product at this account in the window"
    }, "Revenue"))), /*#__PURE__*/React.createElement("tbody", null, topProducts.map(p => {
      const img = p.imageLink || p.imageUrl || p.ImageLink || "";
      return /*#__PURE__*/React.createElement("tr", {
        key: p.productId,
        className: "clickable",
        onClick: () => this.openProduct(p),
        title: `Open ${p.title || p.productId} product detail`
      }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
        className: "dd-prow"
      }, img ? /*#__PURE__*/React.createElement("img", {
        className: "dd-thumb",
        src: img,
        alt: "",
        loading: "lazy"
      }) : /*#__PURE__*/React.createElement("span", {
        className: "dd-thumb dd-thumb-empty"
      }), /*#__PURE__*/React.createElement("span", {
        className: "dd-ptxt"
      }, /*#__PURE__*/React.createElement("span", {
        className: "name"
      }, titleCase(cleanTitle(p.title)) || p.productId), /*#__PURE__*/React.createElement("span", {
        className: "code"
      }, p.sku || p.productId)))), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(p.orders)), /*#__PURE__*/React.createElement("td", {
        className: "n"
      }, num(p.quantity)), /*#__PURE__*/React.createElement("td", {
        className: "n strong"
      }, money(p.totalAmount)));
    })))), topProducts.length === 0 && /*#__PURE__*/React.createElement(Empty, null, "No product sales in this period.")), false ? /*#__PURE__*/React.createElement(TimelineCard, {
      accountNumber: id,
      range: range,
      timeline: x.timeline,
      notes: x.notes,
      span: 12
    }) : null));
  }
}
export default withRouter(CustomerDetailViewComponent);